import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import jobStatuses from '../constants/job-statuses';

function EngineersAnalytics() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('month');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState('2026-04');
  const [selectedYear, setSelectedYear] = useState('2026');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [stats, setStats] = useState({
    currentPeriod: {
      totalEngineers: 0,
      activeEngineers: 0,
      totalJobsCompleted: 0,
      avgJobsPerEngineer: 0,
      totalRevenue: 0,
      avgRevenuePerEngineer: 0,
      totalHours: 0,
      productiveHours: 0,
      travelHours: 0,
      utilization: 0,
      firstTimeFix: 0
    },
    previousPeriod: {
      totalEngineers: 0,
      activeEngineers: 0,
      totalJobsCompleted: 0,
      avgJobsPerEngineer: 0,
      totalRevenue: 0,
      avgRevenuePerEngineer: 0,
      totalHours: 0,
      productiveHours: 0,
      travelHours: 0,
      utilization: 0,
      firstTimeFix: 0
    },
    engineerPerformance: [],
    monthlyTrend: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [viewMode, selectedDate, selectedMonth, selectedYear, customStartDate, customEndDate]);

  const getDateRange = () => {
    let currentStart, currentEnd, prevStart, prevEnd, label;
    const today = new Date();

    switch (viewMode) {
      case 'day':
        currentStart = new Date(selectedDate);
        currentStart.setHours(0, 0, 0, 0);
        currentEnd = new Date(selectedDate);
        currentEnd.setHours(23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setDate(prevStart.getDate() - 1);
        prevEnd = new Date(prevStart);
        prevEnd.setHours(23, 59, 59, 999);
        label = currentStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        break;

      case 'week':
        currentStart = new Date(selectedDate);
        const dayOfWeek = currentStart.getDay();
        currentStart.setDate(currentStart.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
        currentStart.setHours(0, 0, 0, 0);
        currentEnd = new Date(currentStart);
        currentEnd.setDate(currentEnd.getDate() + 6);
        currentEnd.setHours(23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setDate(prevStart.getDate() - 7);
        prevEnd = new Date(currentEnd);
        prevEnd.setDate(prevEnd.getDate() - 7);
        label = `Week of ${currentStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
        break;

      case 'month':
        const [year, month] = selectedMonth.split('-');
        currentStart = new Date(year, month - 1, 1);
        currentEnd = new Date(year, month, 0, 23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setMonth(prevStart.getMonth() - 1);
        prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 1, 0, 23, 59, 59, 999);
        label = currentStart.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        break;

      case 'quarter':
        const currentQuarter = Math.floor((today.getMonth()) / 3);
        currentStart = new Date(selectedYear, currentQuarter * 3, 1);
        currentEnd = new Date(selectedYear, currentQuarter * 3 + 3, 0, 23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setMonth(prevStart.getMonth() - 3);
        prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 3, 0, 23, 59, 59, 999);
        label = `Q${currentQuarter + 1} ${selectedYear}`;
        break;

      case 'year':
        currentStart = new Date(selectedYear, 0, 1);
        currentEnd = new Date(selectedYear, 11, 31, 23, 59, 59, 999);
        prevStart = new Date(parseInt(selectedYear) - 1, 0, 1);
        prevEnd = new Date(parseInt(selectedYear) - 1, 11, 31, 23, 59, 59, 999);
        label = selectedYear;
        break;

      case 'custom':
        if (customStartDate && customEndDate) {
          currentStart = new Date(customStartDate);
          currentEnd = new Date(customEndDate);
          currentEnd.setHours(23, 59, 59, 999);
          const daysDiff = Math.floor((currentEnd - currentStart) / (1000 * 60 * 60 * 24));
          prevStart = new Date(currentStart);
          prevStart.setDate(prevStart.getDate() - daysDiff - 1);
          prevEnd = new Date(prevStart);
          prevEnd.setDate(prevEnd.getDate() + daysDiff);
          label = `${currentStart.toLocaleDateString('en-GB')} - ${currentEnd.toLocaleDateString('en-GB')}`;
        } else {
          return null;
        }
        break;

      default:
        return null;
    }

    return { currentStart, currentEnd, prevStart, prevEnd, label };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      if (!dateRange) {
        setLoading(false);
        return;
      }

      // Fetch people (engineers), jobs, and schedule data
      const [peopleRes, jobsRes, scheduleRes] = await Promise.all([
        apiFetch('/api/people'),
        apiFetch('/api/jobs'),
        apiFetch('/api/schedule')
      ]);

      const people = await peopleRes.json();
      const jobs = await jobsRes.json();
      const schedule = await scheduleRes.json();

      // Filter engineers
      const engineers = people.filter(p => {
        const role = (p.role || p.job_title || '').toLowerCase();
        return role.includes('engineer') || role.includes('technician');
      });

      const currentMetrics = calculateMetrics(engineers, jobs, schedule);
      const previousMetrics = { totalEngineers: 0, activeEngineers: 0, totalJobsCompleted: 0, avgJobsPerEngineer: 0, totalRevenue: 0, avgRevenuePerEngineer: 0, totalHours: 0, productiveHours: 0, travelHours: 0, utilization: 0, firstTimeFix: 0 };

      const engineerPerformance = calculateEngineerPerformance(engineers, jobs, schedule);
      const monthlyTrend = calculateMonthlyTrend(engineers, jobs);

      setStats({
        currentPeriod: currentMetrics,
        previousPeriod: previousMetrics,
        engineerPerformance,
        monthlyTrend
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setLoading(false);
    }
  };

  const calculateMetrics = (engineers, jobs, schedule) => {
    const completedJobs = jobs.filter(j => jobStatuses.isCompleted(j.job_status));
    const totalRevenue = completedJobs.reduce((sum, j) => sum + parseFloat(j.job_value || 0), 0);
    const avgJobsPerEngineer = engineers.length > 0 ? (completedJobs.length / engineers.length).toFixed(1) : 0;
    const avgRevenuePerEngineer = engineers.length > 0 ? (totalRevenue / engineers.length).toFixed(0) : 0;

    // Calculate hours from schedule
    const totalHours = schedule.reduce((sum, s) => sum + (s.duration_hours || 0), 0);
    const productiveHours = totalHours * 0.75; // Estimate
    const travelHours = totalHours * 0.25; // Estimate
    const utilization = totalHours > 0 ? ((productiveHours / totalHours) * 100).toFixed(1) : 0;

    return {
      totalEngineers: engineers.length,
      activeEngineers: engineers.length, // Placeholder - would need activity tracking
      totalJobsCompleted: completedJobs.length,
      avgJobsPerEngineer: parseFloat(avgJobsPerEngineer),
      totalRevenue,
      avgRevenuePerEngineer: parseFloat(avgRevenuePerEngineer),
      totalHours,
      productiveHours,
      travelHours,
      utilization: parseFloat(utilization),
      firstTimeFix: 92.5 // Placeholder
    };
  };

  const calculateEngineerPerformance = (engineers, jobs, schedule) => {
    return engineers.slice(0, 10).map(eng => {
      const engineerJobs = jobs.filter(j => j.assigned_to === eng.id && jobStatuses.isCompleted(j.job_status));
      const revenue = engineerJobs.reduce((sum, j) => sum + parseFloat(j.job_value || 0), 0);
      return {
        name: `${eng.first_name} ${eng.last_name}`,
        jobs: engineerJobs.length,
        revenue
      };
    }).sort((a, b) => b.revenue - a.revenue);
  };

  const calculateMonthlyTrend = (engineers, jobs) => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date();
      date.setMonth(date.getMonth() - i);
      const completedJobs = jobs.filter(j => jobStatuses.isCompleted(j.job_status));
      months.push({
        label: date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
        jobs: completedJobs.length / 6,
        revenue: completedJobs.reduce((sum, j) => sum + parseFloat(j.job_value || 0), 0) / 6
      });
    }
    return months;
  };

  const getTrendArrow = (current, previous) => {
    if (current > previous) return { icon: '↑', color: 'text-green-600' };
    if (current < previous) return { icon: '↓', color: 'text-red-600' };
    return { icon: '→', color: 'text-blue-600' };
  };

  const ComparisonCard = ({ title, currentValue, previousValue, format = '' }) => {
    const trend = getTrendArrow(currentValue, previousValue);

    return (
      <div className="bg-white rounded-lg shadow p-4 border-t-4 border-blue-700">
        <h3 className="text-blue-700 font-semibold mb-3 text-sm">{title}</h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">This Period</span>
            <span className={`font-bold ${trend.color}`}>
              {format}{currentValue.toLocaleString()} {trend.icon}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">Last Period</span>
            <span className="font-semibold text-gray-700">{format}{previousValue.toLocaleString()}</span>
          </div>
        </div>
      </div>
    );
  };

  const SummaryCard = ({ title, value, format = '' }) => (
    <div className="bg-white rounded-lg shadow p-4 border-t-4 border-blue-700">
      <h3 className="text-blue-700 font-semibold mb-3 text-sm">{title}</h3>
      <div className="text-3xl font-bold text-gray-900">{format}{value.toLocaleString()}</div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Engineers Analytics"
        subtitle="Team Performance & Productivity"
        backButton
        onBack={() => navigate('/dashboard')}
        backTitle="Back to Dashboard"
      />

      {/* Filter Bar */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-full mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-600 font-semibold">{getDateRange()?.label || 'Select Date Range'}</div>
            <div className="text-sm text-gray-600">
              Today's Date - {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {['day', 'week', 'month', 'quarter', 'year', 'custom'].map(mode => (
                <button key={mode} onClick={() => setViewMode(mode)}
                  className={`px-3 py-1 rounded text-sm font-medium transition ${
                    viewMode === mode ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-200'
                  }`}>
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>

            {viewMode === 'day' && <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg" />}
            {viewMode === 'week' && <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg" />}
            {viewMode === 'month' && <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg" />}
            {(viewMode === 'quarter' || viewMode === 'year') && (
              <select value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg">
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            )}
            {viewMode === 'custom' && (
              <>
                <input type="date" value={customStartDate} onChange={(e) => setCustomStartDate(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg" />
                <span>to</span>
                <input type="date" value={customEndDate} onChange={(e) => setCustomEndDate(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg" />
              </>
            )}

            <div className="ml-auto text-xs text-gray-500">* All values are excluding VAT</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-6 py-6">
        {loading && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <div className="text-gray-600">Loading analytics data...</div>
          </div>
        )}

        {!loading && (
          <>
        {/* Row 1 - Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard title="Total Engineers" value={stats.currentPeriod.totalEngineers} />
          <SummaryCard title="Jobs Completed" value={stats.currentPeriod.totalJobsCompleted} />
          <SummaryCard title="Total Revenue" value={stats.currentPeriod.totalRevenue} format="£" />
          <SummaryCard title="Avg Revenue/Engineer" value={stats.currentPeriod.avgRevenuePerEngineer} format="£" />
        </div>

        {/* Row 2 - Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <ComparisonCard title="Jobs Per Engineer" currentValue={stats.currentPeriod.avgJobsPerEngineer} previousValue={stats.previousPeriod.avgJobsPerEngineer} />
          <ComparisonCard title="Revenue Per Engineer" currentValue={stats.currentPeriod.avgRevenuePerEngineer} previousValue={stats.previousPeriod.avgRevenuePerEngineer} format="£" />
          <ComparisonCard title="Utilization %" currentValue={stats.currentPeriod.utilization} previousValue={stats.previousPeriod.utilization} format="" />
          <ComparisonCard title="First Time Fix %" currentValue={stats.currentPeriod.firstTimeFix} previousValue={stats.previousPeriod.firstTimeFix} format="" />
        </div>

        {/* Row 3 - Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Top Performers */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Top Performers (Revenue)</h3>
            <div className="space-y-3">
              {stats.engineerPerformance.map((eng, idx) => {
                const maxRevenue = Math.max(...stats.engineerPerformance.map(e => e.revenue), 1);
                return (
                  <div key={idx}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-gray-700">{eng.name}</span>
                      <span className="font-bold text-gray-900">£{eng.revenue.toLocaleString()}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: `${(eng.revenue / maxRevenue) * 100}%` }}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Team Metrics */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Team Metrics</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Productive Time</span>
                  <span className="font-bold text-green-600">{stats.currentPeriod.productiveHours.toFixed(0)}h</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Travel Time</span>
                  <span className="font-bold text-orange-600">{stats.currentPeriod.travelHours.toFixed(0)}h</span>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-600">Total Hours</span>
                  <span className="font-bold text-gray-900">{stats.currentPeriod.totalHours.toFixed(0)}h</span>
                </div>
              </div>
              <div className="pt-4 border-t">
                <div className="text-center">
                  <div className="text-4xl font-bold text-blue-600">{stats.currentPeriod.utilization}%</div>
                  <div className="text-sm text-gray-600 mt-1">Utilization Rate</div>
                </div>
              </div>
              <div className="pt-4 border-t">
                <div className="text-center">
                  <div className="text-3xl font-bold text-green-600">{stats.currentPeriod.firstTimeFix}%</div>
                  <div className="text-sm text-gray-600 mt-1">First Time Fix</div>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Trend */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">6-Month Trend</h3>
            <div className="space-y-3">
              {stats.monthlyTrend.map((month, idx) => {
                const maxValue = Math.max(...stats.monthlyTrend.map(m => Math.max(m.jobs, m.revenue / 100)), 1);
                return (
                  <div key={idx}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">{month.label}</span>
                      <div className="flex gap-3">
                        <span className="text-blue-600 font-semibold">{month.jobs.toFixed(0)} jobs</span>
                        <span className="text-green-600 font-semibold">£{month.revenue.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${(month.jobs / maxValue) * 100}%` }}></div>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div className="bg-green-500 h-2 rounded-full" style={{ width: `${((month.revenue / 100) / maxValue) * 100}%` }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center gap-6 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span className="text-gray-600">Jobs</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span className="text-gray-600">Revenue</span>
              </div>
            </div>
          </div>
        </div>
        </>
        )}
      </main>
    </div>
  );
}

export default EngineersAnalytics;
